/**
 * StrategyPanel — Load strategy and primary keys configuration for a Job.
 *
 * Rendered in the "Strategy" tab of JobEditor.
 *
 * Fields:
 *   - loadStrategy: select (full_replace | incremental_merge | scd1 | scd2)
 *   - primaryKeys: comma-separated text input
 *   - SCD2ConfigPanel (conditionally rendered when job.loadStrategy === "scd2")
 *
 * Save calls PATCH /jobs/{jid} with load_strategy and primary_keys.
 * The SCD2ConfigPanel has its own Save button for SCD2-specific fields.
 *
 * Dirty-state: tracks both the strategy/PK fields AND the SCD2ConfigPanel's
 * dirty state, combining them into a single signal for the parent tab indicator.
 * This prevents the SCD2ConfigPanel's onDirtyChange(false) from clearing the
 * tab's amber dot while the strategy fields themselves are still unsaved.
 *
 * Follows the same onSaved / onDirtyChange / onConflict pattern as other panels.
 */

import { useCallback, useEffect, useId, useState } from "react";
import { Loader2, Save } from "lucide-react";
import type {
  LoadStrategy,
  PEJob,
} from "../../../features/pipeline-engineer/types";
import { updateJob } from "../../../features/pipeline-engineer/api/pe-client";
import { SCD2ConfigPanel } from "./SCD2ConfigPanel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseCSV(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function toCSV(values: string[]): string {
  return values.join(", ");
}

// ---------------------------------------------------------------------------
// Strategy options
// ---------------------------------------------------------------------------

const STRATEGY_OPTIONS: Array<{ value: LoadStrategy; label: string }> = [
  { value: "full_replace", label: "Full Replace" },
  { value: "incremental_merge", label: "Incremental Merge" },
  { value: "scd1", label: "SCD1 — Overwrite" },
  { value: "scd2", label: "SCD2 — Historical" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface StrategyPanelProps {
  job: PEJob;
  pipelineId: string;
  jobId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (isDirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StrategyPanel({
  job,
  pipelineId,
  jobId,
  onSaved,
  onDirtyChange,
  onConflict,
}: StrategyPanelProps) {
  const uid = useId();

  const [loadStrategy, setLoadStrategy] = useState<LoadStrategy>(
    job.loadStrategy,
  );
  const [primaryKeysRaw, setPrimaryKeysRaw] = useState(toCSV(job.primaryKeys));
  const [scd2IsDirty, setScd2IsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Sync local state when job is saved (updatedAt changes) ──────────────
  // Use job.id + job.updatedAt (not the whole `job` object) so we don't reset
  // on every parent re-render — only when the server round-trip actually updates.
  useEffect(() => {
    setLoadStrategy(job.loadStrategy);
    setPrimaryKeysRaw(toCSV(job.primaryKeys));
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dirty detection — own fields only ─────────────────────────────────────
  const ownIsDirty =
    loadStrategy !== job.loadStrategy ||
    primaryKeysRaw !== toCSV(job.primaryKeys);

  // Combined dirty: strategy fields OR SCD2 sub-panel (AC: dirty-state tracking)
  const combinedIsDirty = ownIsDirty || scd2IsDirty;

  useEffect(() => {
    onDirtyChange(combinedIsDirty);
  }, [combinedIsDirty, onDirtyChange]);

  // ── SCD2ConfigPanel callbacks (isolated — doesn't overwrite own dirty) ────
  const handleScd2DirtyChange = useCallback((isDirty: boolean) => {
    setScd2IsDirty(isDirty);
  }, []);

  const handleScd2Saved = useCallback(
    (updated: PEJob) => {
      setScd2IsDirty(false);
      onSaved(updated);
    },
    [onSaved],
  );

  // ── Save (strategy + primary keys only) ───────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updateJob(pipelineId, jobId, {
        load_strategy: loadStrategy,
        primary_keys: parseCSV(primaryKeysRaw),
      });
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [pipelineId, jobId, loadStrategy, primaryKeysRaw, onSaved, onConflict]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Load Strategy */}
      <div className="space-y-1">
        <label
          htmlFor={`${uid}-strategy`}
          className="block text-xs font-medium text-white/50"
        >
          Load Strategy
        </label>
        <select
          id={`${uid}-strategy`}
          value={loadStrategy}
          onChange={(e) => setLoadStrategy(e.target.value as LoadStrategy)}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-brand-500/50 appearance-none"
        >
          {STRATEGY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-gray-900">
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Primary Keys */}
      <div className="space-y-1">
        <label
          htmlFor={`${uid}-pks`}
          className="block text-xs font-medium text-white/50"
        >
          Primary Keys
        </label>
        <input
          id={`${uid}-pks`}
          type="text"
          value={primaryKeysRaw}
          onChange={(e) => setPrimaryKeysRaw(e.target.value)}
          placeholder="e.g. customer_id, order_id"
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
        />
        <p className="text-xs text-white/30">
          Comma-separated column names used to match source rows to target rows.
        </p>
      </div>

      {/* Save error */}
      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!ownIsDirty || saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <Save size={13} aria-hidden="true" />
          )}
          Save Strategy
        </button>
      </div>

      {/* SCD2 Config Sub-Panel — conditionally rendered when job already saved as scd2 */}
      {job.loadStrategy === "scd2" && (
        <SCD2ConfigPanel
          job={job}
          pipelineId={pipelineId}
          jobId={jobId}
          onSaved={handleScd2Saved}
          onDirtyChange={handleScd2DirtyChange}
          onConflict={onConflict}
        />
      )}
    </div>
  );
}
